import React from 'react';

import { CharterPaymentState } from 'Modules/Charter/Types/CharterScheduleItem';
import { Tag } from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/CheckInList/Tag';
import { charterPaymentLabel, charterPaymentTagStyles } from 'Modules/Charter/Utils/charterPaymentLabel';

export interface CharterPaidTagProps {
	/**
	 * Where the money is up to. Absent on a charter with no transaction to read — one booked before
	 * charter payment existed — which reads as unpaid, the same as one that owes money.
	 */
	state?: CharterPaymentState;

	/**
	 * Whether the charter has been handed to accounting, which is shown in place of awaiting.
	 *
	 * Ignored once the charter is paid: money having arrived is the later state, and it is the one
	 * worth reading.
	 */
	invoiced?: boolean;

	/**
	 * Whether the charter has been called off, which outranks everything the money says.
	 *
	 * A charter that is off is off whether or not it was ever paid for, and a cancelled charter that
	 * still read **Paid** would be read as one that is going ahead.
	 */
	cancelled?: boolean;
}

/**
 * What the charter's money is up to, in one word — the same word a booking uses for the same state.
 *
 * Not `BookingStatusTag`: that one reads a booking's transaction status, and its paid state renders
 * as "Booked" for a customer, which is the state of a booking rather than the state of its money. A
 * charter says one thing about the money, in the word for it.
 *
 * The ladder is the booking's, in the booking's order — cancelled, then paid, then the invoice mark,
 * then awaiting an invoice, then owed with no invoice behind it. Both readers are told the same word:
 * a charter for an invoiced account reads **Awaiting** to its customer because their booking for the
 * same arrangement already does, and a charter a manager has marked reads **Invoiced** to both,
 * because a customer told "unpaid" about an invoice their accounts have already been sent has been
 * told the wrong thing.
 *
 * A charter that has been called off says so instead: what happened to the money is beside the point
 * on one that is not going ahead.
 */
export function CharterPaidTag({ state, invoiced = false, cancelled = false }: CharterPaidTagProps) {
	if (cancelled) {
		return (
			<div className="tag__container" data-test-id="charter-paid-tag">
				<Tag text="Cancelled" tagClassName="tag--error" />
			</div>
		);
	}

	return (
		<div className="tag__container" data-test-id="charter-paid-tag">
			<Tag text={charterPaymentLabel(state, invoiced)} customStyles={charterPaymentTagStyles(state, invoiced)} />
		</div>
	);
}
